import type { FactorEntry } from "../services/productFactorsService";

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

export type AnalyzerPeriod = "1 month" | "3 months" | "6 months" | "1 year" | "Since inception" | "Custom";

export interface AnalyzerDateRange {
  startDate: string;
  endDate: string;
}

export interface AnalyzerWindow {
  startMs: number;
  endMs: number;
  requestedStartMs: number | null;
  isTruncated: boolean;
  includeStart?: boolean;
}

export const ANALYZER_PERIODS: { value: AnalyzerPeriod; label: string }[] = [
  { value: "1 month", label: "1M" },
  { value: "3 months", label: "3M" },
  { value: "6 months", label: "6M" },
  { value: "1 year", label: "1Y" },
  { value: "Since inception", label: "All" },
];

export interface AnalyzerPeriodOption {
  value: AnalyzerPeriod;
  label: string;
  isDisabled: boolean;
  disabledReason?: string;
}

const WINDOW_MS_BY_PERIOD: Record<Exclude<AnalyzerPeriod, "Since inception" | "Custom">, number> = {
  "1 month": DAY_MS * 30,
  "3 months": DAY_MS * 90,
  "6 months": DAY_MS * 180,
  "1 year": DAY_MS * 365,
};

function parseTimeMs(value: string): number | null {
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? null : parsed;
}

function getFactorBounds(factors: FactorEntry[]): { firstMs: number; lastMs: number } | null {
  const times = factors
    .map((factor) => parseTimeMs(factor.t))
    .filter((value): value is number => value !== null);

  if (times.length === 0) return null;
  return {
    firstMs: Math.min(...times),
    lastMs: Math.max(...times),
  };
}

export function getAnalyzerPeriodOptionsForFactors(factors: FactorEntry[]): AnalyzerPeriodOption[] {
  const bounds = getFactorBounds(factors);
  if (!bounds) {
    return ANALYZER_PERIODS.map((period) => ({
      ...period,
      isDisabled: false,
    }));
  }

  return ANALYZER_PERIODS.map((period) => {
    if (period.value === "Since inception") {
      return {
        ...period,
        isDisabled: false,
      };
    }

    const requestedStartMs = getAnalyzerPeriodStartMs(period.value, bounds.lastMs);
    const isDisabled = requestedStartMs !== null && requestedStartMs < bounds.firstMs;
    return {
      ...period,
      isDisabled,
      disabledReason: isDisabled
        ? "This period starts before your strategy history begins. Use All for the available history."
        : undefined,
    };
  });
}

export function getAnalyzerPeriodStartMs(
  period: AnalyzerPeriod,
  anchorMs: number
): number | null {
  if (period === "Since inception" || period === "Custom") return null;
  return anchorMs - WINDOW_MS_BY_PERIOD[period];
}

export function getAnalyzerBucketMs(period: AnalyzerPeriod): number {
  if (period === "1 month") return 4 * HOUR_MS;
  return DAY_MS;
}

export function getLatestFactorTimestamp(series: FactorEntry[][]): number | null {
  const latest = series
    .flatMap((factors) => factors)
    .map((factor) => parseTimeMs(factor.t))
    .filter((value): value is number => value !== null);

  if (latest.length === 0) return null;
  return Math.max(...latest);
}

export function filterFactorsForAnalyzerPeriod(
  factors: FactorEntry[],
  period: AnalyzerPeriod,
  anchorMs: number | null
): FactorEntry[] {
  if (anchorMs === null) return factors;
  const startMs = getAnalyzerPeriodStartMs(period, anchorMs);
  if (startMs === null) return factors;

  return factors.filter((factor) => {
    const timeMs = parseTimeMs(factor.t);
    return timeMs !== null && timeMs >= startMs && timeMs <= anchorMs;
  });
}

export function getCommonAnalyzerWindow(
  series: FactorEntry[][],
  period: AnalyzerPeriod,
  customDateRange?: AnalyzerDateRange | null
): AnalyzerWindow | null {
  const customWindow = period === "Custom" ? getAnalyzerCustomWindow(customDateRange) : null;
  if (period === "Custom" && !customWindow) return null;
  const bounds = series.map((factors) => getFactorBounds(factors));

  if (
    bounds.length === 0 ||
    bounds.some((bound): bound is null => bound === null)
  ) {
    return null;
  }

  const completeBounds = bounds.filter(
    (bound): bound is { firstMs: number; lastMs: number } => bound !== null
  );
  const latestCommonEndMs = Math.min(...completeBounds.map((bound) => bound.lastMs));
  const endMs = customWindow ? Math.min(customWindow.endMs, latestCommonEndMs) : latestCommonEndMs;
  const earliestCommonStartMs = Math.max(...completeBounds.map((bound) => bound.firstMs));
  const requestedStartMs = customWindow?.startMs ?? getAnalyzerPeriodStartMs(period, endMs);
  const startMs =
    requestedStartMs === null
      ? earliestCommonStartMs
      : Math.max(requestedStartMs, earliestCommonStartMs);

  if (startMs > endMs) return null;

  return {
    startMs,
    endMs,
    requestedStartMs,
    isTruncated:
      (requestedStartMs !== null && earliestCommonStartMs > requestedStartMs)
      || (customWindow !== null && latestCommonEndMs < customWindow.endMs - DAY_MS + 1),
    includeStart: customWindow !== null || requestedStartMs === null,
  };
}

export function getAnalyzerCustomWindow(range?: AnalyzerDateRange | null): AnalyzerWindow | null {
  if (!range) return null;
  const dates = [range.startDate, range.endDate].map((value) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
    const ms = parseTimeMs(`${value}T00:00:00.000Z`);
    return ms !== null && new Date(ms).toISOString().slice(0, 10) === value ? ms : null;
  });
  const [startMs, endDayMs] = dates;
  if (startMs === null || endDayMs === null || startMs > endDayMs) return null;

  return {
    startMs,
    endMs: endDayMs + DAY_MS - 1,
    requestedStartMs: startMs,
    isTruncated: false,
    includeStart: true,
  };
}

export function filterFactorsForAnalyzerWindow(
  factors: FactorEntry[],
  window: AnalyzerWindow | null
): FactorEntry[] {
  if (window === null) return [];

  return factors.filter((factor) => {
    const timeMs = parseTimeMs(factor.t);
    if (timeMs === null || timeMs > window.endMs) return false;
    return window.includeStart ? timeMs >= window.startMs : timeMs > window.startMs;
  });
}

export function computeFactorCumulativeReturn(factors: FactorEntry[]): number | null {
  if (factors.length === 0) return null;
  const cumulative = factors.reduce((value, factor) => {
    const numericFactor = Number(factor.f);
    return Number.isFinite(numericFactor) ? value * numericFactor : value;
  }, 1);
  return (cumulative - 1) * 100;
}
